/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import {
  PriceCancelled,
  PriceContainer,
  PriceDiscounted,
  PriceItemContainer,
  PriceRRP,
} from './styled';
import { ItemLabel } from '../../../../components/styled';
import { Box, SxProps } from '@mui/material';
import Typography from '../../../../components/Typography';
import { colors } from '@farmlands/mui-theme';
import { formatPrice } from '../../../../util/helpers';

export enum PriceStyle {
  RRP = 'RRP',
  Discounted = 'Discounted',
  Cancelled = 'Cancelled',
}

export type Label = {
  type: (typeof PriceStyle)[keyof typeof PriceStyle];
  label: string;
  value: string;
  valueWithGst?: number;
  testId?: string;
  gstLabel?: string;
};

enum Size {
  Small = 'Small',
  Medium = 'Medium',
  Large = 'Large',
  Default = 'Default',
}

interface Props {
  items: Label[];
  size?: keyof typeof Size;
  sx?: SxProps;
}

export const ItemPrice = ({
  item,
  size,
  label,
}: {
  item: Label;
  size?: keyof typeof Size;
  label?: string;
}) => {
  let itemVariant: 'h2' | 'body2' | 'body1' = size === Size.Large ? 'h2' : 'body2';
  itemVariant = size === Size.Default ? 'body1' : itemVariant;
  switch (item.type) {
    case PriceStyle.RRP:
      return (
        <PriceRRP variant={itemVariant} data-testid={item.testId}>
          <span style={{ textDecoration: 'line-through' }}>{item.value}</span>
          {label && (
            <Typography
              component={'span'}
              sx={{ fontSize: '12px', lineHeight: '18px', textDecoration: 'none' }}
            >
              &nbsp;{label}
            </Typography>
          )}
        </PriceRRP>
      );
    case PriceStyle.Discounted:
      return (
        <PriceDiscounted variant={itemVariant} data-testid={item.testId} component={'span'}>
          {item.value}
          {label && (
            <Typography
              component={'span'}
              sx={{ fontSize: '12px', lineHeight: '18px', color: colors.secondary700 }}
            >
              &nbsp;{label}
            </Typography>
          )}
        </PriceDiscounted>
      );
    case PriceStyle.Cancelled:
      return (
        <PriceCancelled variant={itemVariant} data-testid={item.testId}>
          {item.value}
        </PriceCancelled>
      );
  }
};

export const ProductPriceDetails: React.FC<Props> = ({ items, size, sx }: Props) => {
  const isLg = size === Size.Large;

  return (
    <Box display='flex' flexDirection='column' gap={0.5}>
      <PriceContainer sx={{ columnGap: isLg ? '48px' : '24px', ...sx }}>
        {items.map((x) => (
          <PriceItemContainer key={x.label}>
            <ItemLabel variant='body2' fontWeight={'bold'} color={'grey800'}>
              {x.label}
            </ItemLabel>
            <ItemPrice item={x} size={size} label={x.gstLabel} />
            {x.valueWithGst && (
              <Typography fontSize='12px' lineHeight={'18px'} color='secondary700' mt={0.5}>
                {`(GST incl. ${formatPrice(x.valueWithGst)})`}
              </Typography>
            )}
          </PriceItemContainer>
        ))}
      </PriceContainer>
    </Box>
  );
};
